<script setup lang="ts">
import {
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    ZhEmptyState,
    ZhPageTitle,
    ZhSectionHeading,
    ZhUsageMeter,
} from '@zerohuman/ui'
import { computed, onMounted, ref } from 'vue'
import { api, type StaffPerformanceEnterprise, type StaffPerformanceReport, type StaffPerformanceSpread } from '@/api'
import StaffSlotUseChart from '@/components/StaffSlotUseChart.vue'

/**
 * The Staff area's Performance page: run capacity and pipeline health across every enterprise, so staff see an
 * enterprise at its limit, a long queue or lost work before anyone has to write SQL to find it. Every figure is for
 * the window chosen (the last 24 hours or 7 days) and is read from the runs' own events: only time a run spends
 * running uses a slot. Only a platform admin is offered this page, and the API refuses everybody else.
 */
type Window = StaffPerformanceReport['window']

const report = ref<StaffPerformanceReport | null>(null)
const loading = ref(true)
const error = ref<string | null>(null)
const chosenWindow = ref<Window>('24h')
const enterpriseId = ref<string | null>(null)

const windowWords = computed(() => (chosenWindow.value === '24h' ? 'the last 24 hours' : 'the last 7 days'))
const enterprises = computed(() => report.value?.enterprises ?? [])
const selected = computed(() => report.value?.selected ?? null)
const selectedRow = computed(() => enterprises.value.find(row => row.id === selected.value?.enterpriseId) ?? null)
const maxTaskHours = computed(() => Math.max(0, ...(selected.value?.tasks ?? []).map(task => task.slotHours)))
const commandRows = computed(() => enterprises.value.filter(row => row.commands.count > 0))
const span = computed(() => {
    if (!report.value) {
        return ''
    }
    const from = new Date(report.value.from).toLocaleString()
    const to = new Date(report.value.to).toLocaleString()
    return `${from} to ${to}`
})

async function load(): Promise<void> {
    loading.value = true
    error.value = null
    try {
        report.value = await api.staffPerformance(chosenWindow.value, enterpriseId.value)
        enterpriseId.value = report.value.selected?.enterpriseId ?? enterpriseId.value
    } catch (failure) {
        error.value = failure instanceof Error ? failure.message : 'Could not load the performance figures.'
    } finally {
        loading.value = false
    }
}

function pickWindow(next: Window): void {
    if (next !== chosenWindow.value) {
        chosenWindow.value = next
        void load()
    }
}

function pickEnterprise(event: Event): void {
    enterpriseId.value = (event.target as HTMLSelectElement).value || null
    void load()
}

/** Hours to one decimal place. */
function hours(value: number): string {
    return value.toFixed(1)
}

function minutes(value: number): string {
    return value < 10 ? value.toFixed(1) : value.toFixed(0)
}

function spread(value: StaffPerformanceSpread): string {
    return `${minutes(value.p50Minutes)} / ${minutes(value.p90Minutes)} / ${minutes(value.maxMinutes)}`
}

/** A queue now: how many runs wait, and how many have waited past half an hour while a slot stood free. */
function queuedWords(row: StaffPerformanceEnterprise): string {
    const { total, longWaiting, longWaitingWithFreeSlot } = row.queuedNow
    if (total === 0) {
        return 'none'
    }
    const long = longWaiting ? `, ${longWaiting} over 30 min` : ''
    const free = longWaitingWithFreeSlot ? ` (${longWaitingWithFreeSlot} with a slot free)` : ''
    return `${total}${long}${free}`
}

onMounted(load)
</script>

<template>
	<section class="w-full space-y-8 p-8">
		<div class="space-y-3">
			<ZhPageTitle kicker="Staff">Performance</ZhPageTitle>
			<p class="text-muted-foreground max-w-3xl text-sm">
				Run capacity and pipeline health across every enterprise. A <strong>slot-hour</strong> is one run
				running for one hour: an enterprise with a limit of 6 has 144 slot-hours a day. Only time a run is
				running uses a slot. Time parked on a gate or waiting on another run holds none, so it is shown
				separately and never counted as slot use.
			</p>
			<div class="flex flex-wrap items-center gap-3">
				<div class="flex gap-1" role="group" aria-label="Window">
					<Button
						type="button"
						size="sm"
						:variant="chosenWindow === '24h' ? 'default' : 'outline'"
						:aria-pressed="chosenWindow === '24h'"
						data-testid="performance-window-24h"
						@click="pickWindow('24h')"
					>
						Last 24 hours
					</Button>
					<Button
						type="button"
						size="sm"
						:variant="chosenWindow === '7d' ? 'default' : 'outline'"
						:aria-pressed="chosenWindow === '7d'"
						data-testid="performance-window-7d"
						@click="pickWindow('7d')"
					>
						Last 7 days
					</Button>
				</div>
				<label v-if="enterprises.length" class="flex items-center gap-2 text-sm">
					Break down
					<select
						:value="enterpriseId ?? ''"
						class="border-input bg-background rounded-md border px-2 py-1 text-sm"
						data-testid="performance-enterprise"
						@change="pickEnterprise"
					>
						<option v-for="row in enterprises" :key="row.id" :value="row.id">{{ row.name }}</option>
					</select>
				</label>
				<span v-if="report" class="text-muted-foreground text-xs" data-testid="performance-span">
					{{ span }} · read again every {{ Math.round(report.cacheSeconds / 60) }} minutes
				</span>
			</div>
		</div>

		<p v-if="error" class="text-destructive text-sm" role="alert">{{ error }}</p>
		<p v-if="loading && !report" class="text-muted-foreground text-sm">Loading…</p>
		<ZhEmptyState v-if="report && !enterprises.length && !loading">No runs in {{ windowWords }}.</ZhEmptyState>

		<template v-if="report && enterprises.length">
			<section class="space-y-3" aria-labelledby="perf-slots">
				<ZhSectionHeading id="perf-slots">Slot use against the limit, {{ windowWords }}</ZhSectionHeading>
				<p class="text-muted-foreground text-sm">
					Bars are the average number of runs running in each hour; the dashed line is the enterprise's
					concurrent-run limit now. "Idle while queued" is time a slot stood free while runs of the enterprise
					waited in line for one.
				</p>
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>Enterprise</TableHead>
							<TableHead class="min-w-64">Running each hour</TableHead>
							<TableHead>Limit</TableHead>
							<TableHead>Slot-hours used / available</TableHead>
							<TableHead>Parked hours</TableHead>
							<TableHead>Idle while queued</TableHead>
							<TableHead>Queued now</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						<TableRow v-for="row in enterprises" :key="row.id" data-testid="performance-enterprise-row">
							<TableCell class="font-medium">{{ row.name }}</TableCell>
							<TableCell>
								<StaffSlotUseChart :hourly="row.hourly" :limit="row.limit" :window-words="windowWords" />
							</TableCell>
							<TableCell class="tabular-nums">{{ row.limit }}</TableCell>
							<TableCell class="min-w-48">
								<ZhUsageMeter
									:used="Number(hours(row.slotHoursUsed))"
									:included="row.slotHoursAvailable"
									unit="slot-hours"
								/>
								<span class="text-muted-foreground text-xs">{{ row.percentUsed }}% used</span>
							</TableCell>
							<TableCell class="tabular-nums">{{ hours(row.parkedHours) }} h</TableCell>
							<TableCell class="tabular-nums">
								{{ hours(row.idleWhileQueued.hours) }} h
								<span class="text-muted-foreground text-xs">
									({{ hours(row.idleWhileQueued.slotHours) }} slot-hours free)
								</span>
							</TableCell>
							<TableCell class="tabular-nums" data-testid="performance-queued-now">{{ queuedWords(row) }}</TableCell>
						</TableRow>
					</TableBody>
				</Table>
			</section>

			<section v-if="selected" class="space-y-3" aria-labelledby="perf-tasks">
				<ZhSectionHeading id="perf-tasks">Slot-hours by task: {{ selected.name }}, {{ windowWords }}</ZhSectionHeading>
				<p class="text-muted-foreground text-sm">
					Slot-hours are time running. Parked hours are time the same runs spent on a gate or waiting on
					another run, and use no slot.
				</p>
				<Table v-if="selected.tasks.length">
					<TableHeader>
						<TableRow>
							<TableHead>Task</TableHead>
							<TableHead>Runs</TableHead>
							<TableHead class="min-w-48">Slot-hours</TableHead>
							<TableHead>Minutes running per run</TableHead>
							<TableHead>Parked hours</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						<TableRow v-for="task in selected.tasks" :key="task.taskId" data-testid="performance-task-row">
							<TableCell>{{ task.name }}</TableCell>
							<TableCell class="tabular-nums">{{ task.runs }}</TableCell>
							<TableCell>
								<div class="flex items-center gap-2">
									<span
										class="bg-chart-2 block h-2"
										:style="{ width: `${maxTaskHours > 0 ? (task.slotHours / maxTaskHours) * 8 : 0}rem` }"
										aria-hidden="true"
									/>
									<span class="tabular-nums">{{ hours(task.slotHours) }}</span>
								</div>
							</TableCell>
							<TableCell class="tabular-nums">{{ minutes(task.minutesPerRun) }}</TableCell>
							<TableCell class="tabular-nums">{{ hours(task.parkedHours) }}</TableCell>
						</TableRow>
					</TableBody>
				</Table>
				<ZhEmptyState v-else>No run of {{ selected.name }} ran or parked in {{ windowWords }}.</ZhEmptyState>
			</section>

			<section class="space-y-3" aria-labelledby="perf-wait">
				<ZhSectionHeading id="perf-wait">Queue wait, {{ windowWords }}</ZhSectionHeading>
				<p class="text-muted-foreground text-sm">
					From a run's creation to its first start, for the runs that first started in the window, in minutes as
					median / 90th percentile / longest. Split into waiting for an enterprise slot and waiting for the
					member, whose own run slots were all taken.
				</p>
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>Enterprise</TableHead>
							<TableHead>Runs started</TableHead>
							<TableHead>Total wait</TableHead>
							<TableHead>Waiting for a slot</TableHead>
							<TableHead>Waiting for the member</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						<TableRow v-for="row in enterprises" :key="row.id" data-testid="performance-wait-row">
							<TableCell class="font-medium">{{ row.name }}</TableCell>
							<TableCell class="tabular-nums">{{ row.queueWait.runs }}</TableCell>
							<TableCell class="tabular-nums">{{ spread(row.queueWait.total) }}</TableCell>
							<TableCell class="tabular-nums">{{ spread(row.queueWait.slot) }}</TableCell>
							<TableCell class="tabular-nums">{{ spread(row.queueWait.member) }}</TableCell>
						</TableRow>
					</TableBody>
				</Table>
			</section>

			<section class="space-y-3" aria-labelledby="perf-commands">
				<ZhSectionHeading id="perf-commands">Workspace command time, {{ windowWords }}</ZhSectionHeading>
				<p class="text-muted-foreground text-sm">
					Time spent in workspace commands on the shared run host, by enterprise. A time-out is a command that
					said it timed out, or exited with code 124. Commands are grouped by the first line they printed.
				</p>
				<Table v-if="commandRows.length">
					<TableHeader>
						<TableRow>
							<TableHead>Enterprise</TableHead>
							<TableHead>Commands</TableHead>
							<TableHead>Command hours</TableHead>
							<TableHead>Share of the host</TableHead>
							<TableHead>Time-outs</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						<TableRow v-for="row in commandRows" :key="row.id" data-testid="performance-command-row">
							<TableCell class="font-medium">{{ row.name }}</TableCell>
							<TableCell class="tabular-nums">{{ row.commands.count }}</TableCell>
							<TableCell class="tabular-nums">{{ hours(row.commands.hours) }}</TableCell>
							<TableCell class="tabular-nums">{{ row.commands.share }}%</TableCell>
							<TableCell class="tabular-nums">
								{{ row.commands.timeouts }}
								<span class="text-muted-foreground text-xs">({{ hours(row.commands.timeoutHours) }} h)</span>
							</TableCell>
						</TableRow>
					</TableBody>
				</Table>
				<ZhEmptyState v-else>No workspace commands ran in {{ windowWords }}.</ZhEmptyState>
				<template v-if="selectedRow && selectedRow.commands.top.length">
					<h3 class="text-sm font-medium">Top command kinds: {{ selectedRow.name }}, {{ windowWords }}</h3>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>First line printed</TableHead>
								<TableHead>Commands</TableHead>
								<TableHead>Hours</TableHead>
								<TableHead>Time-outs</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							<TableRow
								v-for="kind in selectedRow.commands.top"
								:key="kind.label"
								data-testid="performance-command-kind"
							>
								<TableCell><code class="text-xs">{{ kind.label }}</code></TableCell>
								<TableCell class="tabular-nums">{{ kind.count }}</TableCell>
								<TableCell class="tabular-nums">{{ hours(kind.hours) }}</TableCell>
								<TableCell class="tabular-nums">{{ kind.timeouts }}</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</template>
			</section>

			<section class="space-y-3" aria-labelledby="perf-lost">
				<ZhSectionHeading id="perf-lost">Lost work, {{ windowWords }}</ZhSectionHeading>
				<p class="text-muted-foreground text-sm">
					Runs that failed in the window, by reason, with the slot-hours they had run before failing. Worker
					retirements are worker processes told to make way for a new release or a restart.
				</p>
				<div class="grid gap-6 lg:grid-cols-[2fr_1fr]">
					<Table v-if="report.lostWork.byReason.length">
						<TableHeader>
							<TableRow>
								<TableHead>Reason</TableHead>
								<TableHead>Runs failed</TableHead>
								<TableHead>Slot-hours spent</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							<TableRow
								v-for="row in report.lostWork.byReason"
								:key="row.reason"
								data-testid="performance-lost-row"
							>
								<TableCell><code class="text-xs">{{ row.reason }}</code></TableCell>
								<TableCell class="tabular-nums">{{ row.runs }}</TableCell>
								<TableCell class="tabular-nums">{{ hours(row.slotHours) }}</TableCell>
							</TableRow>
						</TableBody>
					</Table>
					<ZhEmptyState v-else>No run failed in {{ windowWords }}.</ZhEmptyState>
					<Table v-if="report.lostWork.retirementsByDay.length">
						<TableHeader>
							<TableRow>
								<TableHead>Day (UTC)</TableHead>
								<TableHead>Worker retirements</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							<TableRow v-for="row in report.lostWork.retirementsByDay" :key="row.day">
								<TableCell>{{ row.day }}</TableCell>
								<TableCell class="tabular-nums">{{ row.count }}</TableCell>
							</TableRow>
						</TableBody>
					</Table>
					<ZhEmptyState v-else>No worker retirements recorded in {{ windowWords }}.</ZhEmptyState>
				</div>
			</section>

			<section v-if="selected" class="space-y-3" aria-labelledby="perf-members">
				<ZhSectionHeading id="perf-members">Slot use by member: {{ selected.name }}, {{ windowWords }}</ZhSectionHeading>
				<p class="text-muted-foreground text-sm">
					Each member's slot-hours against their own cap of runs at once over the window.
				</p>
				<Table v-if="selected.members.length">
					<TableHeader>
						<TableRow>
							<TableHead>Member</TableHead>
							<TableHead>Cap</TableHead>
							<TableHead class="min-w-48">Slot-hours used of their cap</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						<TableRow v-for="member in selected.members" :key="member.memberId" data-testid="performance-member-row">
							<TableCell>{{ member.name }}</TableCell>
							<TableCell class="tabular-nums">{{ member.cap }}</TableCell>
							<TableCell>
								<ZhUsageMeter
									:used="Number(hours(member.slotHours))"
									:included="member.cap * (chosenWindow === '24h' ? 24 : 168)"
									unit="slot-hours"
								/>
								<span class="text-muted-foreground text-xs">{{ member.percentOfCap }}% of their cap</span>
							</TableCell>
						</TableRow>
					</TableBody>
				</Table>
				<ZhEmptyState v-else>No member of {{ selected.name }} ran work in {{ windowWords }}.</ZhEmptyState>
			</section>

			<section v-if="selected" class="space-y-3" aria-labelledby="perf-pipelines">
				<ZhSectionHeading id="perf-pipelines">Pipelines and reruns: {{ selected.name }}, {{ windowWords }}</ZhSectionHeading>
				<p class="text-muted-foreground text-sm" data-testid="performance-pipeline-summary">
					{{ selected.pipelines.runs }} runs on {{ selected.pipelines.count }} pipelines were created in the
					window. A rerun is a second or later run of the same task on the same pipeline.
				</p>
				<div v-if="selected.pipelines.count" class="grid gap-6 lg:grid-cols-2">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Pipeline</TableHead>
								<TableHead>Runs</TableHead>
								<TableHead>Stages</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							<TableRow v-for="pipeline in selected.pipelines.top" :key="pipeline.pipelineKey">
								<TableCell><code class="text-xs">{{ pipeline.pipelineKey }}</code></TableCell>
								<TableCell class="tabular-nums">{{ pipeline.runs }}</TableCell>
								<TableCell class="tabular-nums">{{ pipeline.stages }}</TableCell>
							</TableRow>
						</TableBody>
					</Table>
					<Table v-if="selected.pipelines.reruns.length">
						<TableHeader>
							<TableRow>
								<TableHead>Most-rerun stage</TableHead>
								<TableHead>Reruns</TableHead>
								<TableHead>Pipelines</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							<TableRow
								v-for="stage in selected.pipelines.reruns"
								:key="stage.taskId"
								data-testid="performance-rerun-row"
							>
								<TableCell>{{ stage.name }}</TableCell>
								<TableCell class="tabular-nums">{{ stage.reruns }}</TableCell>
								<TableCell class="tabular-nums">{{ stage.pipelines }}</TableCell>
							</TableRow>
						</TableBody>
					</Table>
					<ZhEmptyState v-else>No stage ran twice on one pipeline in {{ windowWords }}.</ZhEmptyState>
				</div>
			</section>
		</template>
	</section>
</template>
